<template>
  <div class="welcome-container">
    <div class="animated-bg"></div>

    <div class="welcome-content">
      <!-- Hero Section -->
      <section class="hero-section glass-card">
        <div class="hero-badge">
          <v-icon small>mdi-heart</v-icon>
          <span>Every major trend has winners and losers. We talk to the losers.</span>
        </div>

        <h1 class="hero-title">
          <span class="brand-name">The Human Cost</span>
          <span class="tagline">"Progress without humanity isn't progress at all."</span>
        </h1>

        <p class="hero-subtitle">
          Behind every economic report, technological breakthrough, and policy change are real people whose lives get turned upside down. While experts debate statistics, families lose their homes. We tell the stories data can't capture.
        </p>

        <div class="hero-cta">
          <v-btn
            color="primary"
            x-large
            rounded
            elevation="0"
            @click="watchLatest"
            class="primary-cta glass-button"
          >
            <v-icon left>mdi-play-circle</v-icon>
            Watch Latest Stories
          </v-btn>

          <v-btn
            color="secondary"
            x-large
            rounded
            outlined
            @click="submitStory"
            class="secondary-cta"
          >
            <v-icon left>mdi-email</v-icon>
            Email Your Story
          </v-btn>
        </div>

        <!-- Impact Stats -->
        <div class="impact-stats">
          <div v-for="stat in impactStats" :key="stat.label" class="stat-item">
            <span class="stat-number">{{ stat.number }}</span>
            <span class="stat-label">{{ stat.label }}</span>
          </div>
        </div>
      </section>

      <!-- What We Do Section -->
      <section class="what-we-do-section glass-card">
        <h2 class="section-title gradient-text">What We Do</h2>
        <div class="what-we-do-grid">
          <div v-for="feature in features" :key="feature.title" class="feature-card">
            <div class="feature-icon">
              <v-icon :color="feature.color" size="48">{{ feature.icon }}</v-icon>
            </div>
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.description }}</p>
          </div>
        </div>
      </section>

      <!-- Recent Stories Section -->
      <section class="recent-stories-section">
        <h2 class="section-title gradient-text">Recent Stories</h2>
        <div class="stories-grid">
          <div v-for="story in recentStories" :key="story.id" class="story-card glass-card">
            <div class="story-icon">
              <v-icon :color="story.color" size="32">{{ story.icon }}</v-icon>
            </div>
            <h4>{{ story.title }}</h4>
            <p>{{ story.description }}</p>
            <div class="story-impact">
              <span class="impact-tag">{{ story.impact }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Share Your Story Section -->
      <section class="share-story-section glass-card">
        <h2 class="section-title gradient-text">Your Story Matters</h2>
        <div class="share-content">
          <div class="share-text">
            <h3>Have you been affected by major economic or technological changes?</h3>
            <p>
              We want to hear from you. Whether you've lost a job to AI, struggled with rising costs,
              or found your industry transformed overnight - your story helps others understand what's
              really happening around the world.
            </p>

            <div class="story-types">
              <div v-for="type in storyTypes" :key="type.text" class="story-type">
                <v-icon :color="type.color">{{ type.icon }}</v-icon>
                <span>{{ type.text }}</span>
              </div>
            </div>
          </div>

          <div class="share-form">
            <v-btn
              color="primary"
              x-large
              block
              @click="submitStory"
              class="share-cta"
            >
              <v-icon left class="black--text">mdi-email</v-icon>
              <span class="black--text">Email Your Story</span>
            </v-btn>
            <p class="privacy-note">
              Send us your story via email - we'll analyze it while protecting your privacy
            </p>
          </div>
        </div>
      </section>

      <!-- Our Mission Section -->
      <section class="mission-section glass-card">
        <h2 class="section-title gradient-text">Our Mission</h2>
        <div class="mission-content">
          <p class="mission-statement">
            The families who understand these economic and technological shifts early will adapt and thrive.
            Those who ignore the human cost will be blindsided when change hits their own lives.
          </p>

          <div class="mission-points">
            <div v-for="point in missionPoints" :key="point.text" class="mission-point">
              <v-icon :color="point.color">{{ point.icon }}</v-icon>
              <span>{{ point.text }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Bottom CTA -->
      <section class="bottom-cta glass-card">
        <h2 class="cta-title">Never Be Caught Off Guard By Change Again</h2>
        <p class="cta-subtitle">
          For families, workers, and anyone who wants to understand what's really happening globally -
          beyond the headlines.
        </p>

        <div class="final-ctas">
          <v-btn
            color="primary"
            x-large
            rounded
            @click="watchLatest"
            class="final-cta black--text"
          >
            Watch Our Stories
          </v-btn>

          <v-btn
            color="secondary"
            x-large
            rounded
            outlined
            @click="submitStory"
            class="final-cta-secondary black--text"
          >
            <v-icon left class="black--text">mdi-email</v-icon>
            Email Your Story
          </v-btn>
        </div>

        <div class="trust-indicators">
          <div v-for="indicator in trustIndicators" :key="indicator.text" class="trust-item">
            <v-icon small>{{ indicator.icon }}</v-icon>
            <span>{{ indicator.text }}</span>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TheHumanCostWelcome',

  data() {
    return {
      impactStats: [
        { number: '40K+', label: 'Weekly Viewers' },
        { number: '93%', label: 'Organic Reach' },
        { number: 'Daily', label: 'New Stories' }
      ],

      features: [
        {
          title: 'Find the Real Stories',
          description: 'Behind every headline trend, we find the people actually living through it - not the CEOs or politicians who benefit.',
          icon: 'mdi-account-search',
          color: 'error'
        },
        {
          title: 'Multi-Perspective Analysis',
          description: 'Expert analysis of big-picture trends combined with real conversations from people whose lives are affected.',
          icon: 'mdi-forum',
          color: 'warning'
        },
        {
          title: 'Early Warning System',
          description: 'Help families understand and adapt to changes before they\'re blindsided by economic and technological shifts.',
          icon: 'mdi-chart-line',
          color: 'success'
        }
      ],

      recentStories: [
        {
          id: 1,
          title: "Sarah vs. Taylor Swift's $400M",
          description: "Meet the indie musician competing against mega-corporate resources in today's music industry",
          icon: "mdi-music",
          color: "primary",
          impact: "Music Industry"
        },
        {
          id: 2,
          title: "From Software Engineer to DoorDash",
          description: "Shawn lost his 10-year tech career to AI and now delivers food to survive",
          icon: "mdi-car",
          color: "warning",
          impact: "AI Displacement"
        },
        {
          id: 3,
          title: "15 Years Replaced by $20/Month",
          description: "Anya's marketing career ended when her company chose an AI subscription over human expertise",
          icon: "mdi-briefcase-remove",
          color: "error",
          impact: "Job Automation"
        },
        {
          id: 4,
          title: "Climate Refugees in Switzerland",
          description: "How glacier collapse forced an entire community to abandon their homes and livelihoods",
          icon: "mdi-glacier",
          color: "info",
          impact: "Climate Change"
        }
      ],

      storyTypes: [
        {
          icon: 'mdi-robot',
          color: 'primary',
          text: 'Job displacement from AI/automation'
        },
        {
          icon: 'mdi-home',
          color: 'secondary',
          text: 'Housing market impacts'
        },
        {
          icon: 'mdi-trending-down',
          color: 'success',
          text: 'Industry changes and layoffs'
        },
        {
          icon: 'mdi-cash-minus',
          color: 'warning',
          text: 'Economic policy impacts'
        }
      ],

      missionPoints: [
        {
          icon: 'mdi-shield-account',
          color: 'primary',
          text: 'Give voice to the voiceless in major economic trends'
        },
        {
          icon: 'mdi-lightbulb-on',
          color: 'secondary',
          text: 'Help families adapt to change before it hits them'
        },
        {
          icon: 'mdi-scale-balance',
          color: 'success',
          text: 'Show the real impact behind policy debates and statistics'
        }
      ],

      trustIndicators: [
        { icon: 'mdi-heart', text: 'Real People' },
        { icon: 'mdi-shield-check', text: 'Privacy Protected' },
        { icon: 'mdi-account-group', text: 'Community Focused' }
      ]
    };
  },

  methods: {
    watchLatest() {
      // Navigate to YouTube channel or video section
      window.open('https://www.youtube.com/playlist?list=PLeVCY1iy_lyFp-HnwTaGk4ygXDXAtAhMW', '_blank');
    },

    submitStory() {
      // Open default email client with pre-filled subject and body
      const subject = encodeURIComponent('My Story for The Human Cost Analysis');
      const body = encodeURIComponent(`Hi,

I'd like to share my story for potential analysis on The Human Cost:

Story Category: [AI displacement / Housing crisis / Industry change / Economic policy impact / Other]

What happened:


When it started:


How it affected me/my family:


What I wish people understood:


[Feel free to contact me if you need more details]

Best regards`);

      window.location.href = `mailto:stories@unstruct.ai?subject=${subject}&body=${body}`;
    }
  }
};
</script>

<style scoped>
.welcome-container {
  position: relative;
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%);
  color: white;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.animated-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background:
    radial-gradient(circle at 20% 80%, rgba(15, 12, 41, 0.6) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(48, 43, 99, 0.6) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(36, 36, 62, 0.6) 0%, transparent 50%);
  animation: pulse 4s ease-in-out infinite alternate;
}

@keyframes pulse {
  0% { opacity: 0.8; }
  100% { opacity: 1; }
}

.welcome-content {
  position: relative;
  z-index: 1;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.glass-card {
  backdrop-filter: blur(20px);
  background: rgba(15, 12, 41, 0.7);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 3rem;
  padding: 3rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}

.glass-card:hover {
  background: rgba(15, 12, 41, 0.85);
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}

/* Hero Section */
.hero-section {
  text-align: center;
  padding: 4rem 3rem;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 107, 107, 0.2);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-radius: 50px;
  padding: 0.75rem 1.5rem;
  margin-bottom: 2rem;
  font-size: 0.9rem;
  font-weight: 500;
}

.hero-title {
  margin-bottom: 1.5rem;
}

.brand-name {
  display: block;
  font-size: 4rem;
  font-weight: 800;
  background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.5rem;
}

.tagline {
  display: block;
  font-size: 1.2rem;
  font-weight: 400;
  opacity: 0.9;
  font-style: italic;
}

.hero-subtitle {
  font-size: 1.25rem;
  line-height: 1.6;
  opacity: 0.9;
  max-width: 800px;
  margin: 0 auto 3rem;
}

.hero-cta {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;
}

.glass-button {
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.impact-stats {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 2.5rem;
  font-weight: 700;
  color: #4ecdc4;
}

.stat-label {
  display: block;
  font-size: 0.9rem;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Section Titles */
.section-title {
  font-size: 3rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 3rem;
}

.gradient-text {
  background: linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* What We Do Section */
.what-we-do-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.feature-card {
  text-align: center;
  padding: 2rem;
  border-radius: 16px;
  background: rgba(15, 12, 41, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.feature-card:hover {
  background: rgba(15, 12, 41, 0.6);
  transform: translateY(-4px);
}

.feature-icon {
  margin-bottom: 1.5rem;
}

.feature-card h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.feature-card p {
  opacity: 0.85;
  line-height: 1.6;
}

/* Recent Stories Section */
.recent-stories-section {
  background: rgba(255, 255, 255, 0.03);
}

.stories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.story-card {
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
}

.story-card:hover {
  transform: translateY(-4px);
}

.story-icon {
  margin-bottom: 1rem;
}

.story-card h4 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.story-card p {
  opacity: 0.85;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.impact-tag {
  background: rgba(78, 205, 196, 0.2);
  border: 1px solid rgba(78, 205, 196, 0.3);
  border-radius: 20px;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Share Story Section */
.share-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  align-items: center;
}

.share-text h3 {
  font-size: 1.75rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.share-text p {
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.story-types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.story-type {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(15, 12, 41, 0.4);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.story-type:hover {
  background: rgba(15, 12, 41, 0.6);
}

.share-form {
  text-align: center;
}

.share-cta {
  margin-bottom: 1rem;
}

.privacy-note {
  font-size: 0.85rem;
  opacity: 0.7;
  line-height: 1.4;
}

/* Mission Section */
.mission-statement {
  font-size: 1.5rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 3rem;
  opacity: 0.95;
}

.mission-points {
  display: grid;
  gap: 1.5rem;
}

.mission-point {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: rgba(15, 12, 41, 0.4);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.mission-point:hover {
  background: rgba(15, 12, 41, 0.6);
  transform: translateX(4px);
}

.mission-point span {
  font-size: 1.1rem;
  font-weight: 500;
}

/* Bottom CTA */
.bottom-cta {
  text-align: center;
  padding: 4rem 3rem;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
}

.cta-subtitle {
  font-size: 1.25rem;
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.final-ctas {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 3rem;
}

.trust-indicators {
  display: flex;
  justify-content: center;
  gap: 2rem;
  flex-wrap: wrap;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 0.8;
  font-size: 0.9rem;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .welcome-content {
    padding: 1rem;
  }

  .glass-card {
    padding: 2rem;
    margin-bottom: 2rem;
  }

  .hero-section {
    padding: 3rem 2rem;
  }

  .brand-name {
    font-size: 2.5rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .share-content {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .story-types {
    grid-template-columns: 1fr;
  }

  .impact-stats {
    gap: 2rem;
  }

  .hero-cta {
    flex-direction: column;
    align-items: center;
  }

  .final-ctas {
    flex-direction: column;
    align-items: center;
  }

  .trust-indicators {
    gap: 1rem;
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 480px) {
  .hero-subtitle {
    font-size: 1.1rem;
  }

  .brand-name {
    font-size: 2rem;
  }

  .tagline {
    font-size: 1rem;
  }

  .cta-title {
    font-size: 2rem;
  }

  .mission-statement {
    font-size: 1.25rem;
  }
}
</style>
